import { Session } from '@/common/types';
import { SubmitVehicleCostSavingsErrors } from '../utils/actions/submit-add-savings-calculator-params';
import { useFetcher } from 'react-router';
import { Vehicle } from '../types';

export interface SavingsCalculatorFetcher {
  vehicle: Vehicle;
  errors: SubmitVehicleCostSavingsErrors;
}

interface SavingsCalculatorFetcherProps {
  vehicleId: number;
  creditScore?: string;
  atOrOver25?: boolean;
  milesDrivenPerMonth?: number;
  ownershipYears?: number;
  marketId?: string;
  intent?: string;
}

interface UseSavingsCalculatorFetcherProps {
  setSavingsCalculatorVehicleId: (id: number) => void;
  setSavingsCalculatorOpen: (open: boolean) => void;
}

/**
 * Hook to fetch savings calculator data for a vehicle on inventory page. Provides
 * a `savingsCalculatorFetcher` function to fetch savings calculator data in a controlled way as
 * well as the raw `savingsCalculatorSubmit` function to be used from within the calculator form.
 */
export function useSavingsCalculatorFetcher({
  setSavingsCalculatorVehicleId,
  setSavingsCalculatorOpen,
}: UseSavingsCalculatorFetcherProps) {
  const { submit, data: savingsCalculatorData } = useFetcher<SavingsCalculatorFetcher>();

  function savingsCalculatorFetcher({
    intent = 'fetch_savings_calculator',
    vehicleId,
    // Note: These defaults reflect the server defaults
    creditScore = 'excellent',
    atOrOver25 = true,
    milesDrivenPerMonth = 850,
    ownershipYears = 5,
    marketId = '',
  }: SavingsCalculatorFetcherProps) {
    submit(
      {
        intent,
        vehicleId,
        creditScore,
        atOrOver25,
        milesDrivenPerMonth,
        ownershipYears,
        marketId,
      },
      { method: 'post' }
    );
  }

  const fetchSavingsCalculator =
    (vehicle: Vehicle, session: Session, market?: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      setSavingsCalculatorVehicleId(vehicle.id);
      savingsCalculatorFetcher({
        vehicleId: vehicle.id,
        creditScore: session?.cart?.credit_score_id,
        atOrOver25: session?.cart?.is_over_25,
        milesDrivenPerMonth: session?.cart?.plan_mileage,
        marketId: market,
      });
      setSavingsCalculatorOpen(true);
    };

  return {
    savingsCalculatorData,
    fetchSavingsCalculator,
    savingsCalculatorSubmit: submit,
  };
}
